import { useContext, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { claimOrderByLabel } from '../../services/shipStationAPI';
import { useAuth } from '../../contexts/AuthContext';
import { OrderContext } from '../../contexts/OrderContext';
import { getApiErrorMessage } from '../../services/errorUtils';
import { buildPickNextOrderNavState } from './orderdetail/orderDetailNavState';
import { prefetchOrderDetail } from './orderdetail/orderDetailPrefetch';
import { QR_NOT_DETECTED_MESSAGE } from '../scanner/qrScanMessages';
import { OrderLabelScanSheet } from './OrderLabelScanSheet';
import { useWorkerUserTag } from './useWorkerUserTag';
import axios from 'axios';

const ClaimOrderScanner = (): ReactElement | null => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const orderCtx = useContext(OrderContext);
  const setSelectedOrderId = orderCtx?.setSelectedOrderId;
  const setOrderItems = orderCtx?.setOrderItems;
  const setShipToName = orderCtx?.setShipToName;
  const { loading, error: tagError, userTag } = useWorkerUserTag(user?.email);
  const [scanError, setScanError] = useState('');
  const [claiming, setClaiming] = useState(false);
  const pendingRef = useRef(false);
  const claimedOrderIdRef = useRef<string | number | null>(null);

  const openOrder = (): void => {
    const orderId = claimedOrderIdRef.current;
    if (orderId == null) return;
    setSelectedOrderId?.(orderId);
    setOrderItems?.([]);
    setShipToName?.('');
    navigate(`/order/${orderId}`, {
      replace: true,
      state: buildPickNextOrderNavState(),
    });
  };

  const handleQrCapture = async (qrData: string | null): Promise<boolean> => {
    if (pendingRef.current || claiming || userTag == null) return false;
    const payload = qrData?.trim() ?? '';
    if (!payload) {
      setScanError(QR_NOT_DETECTED_MESSAGE);
      return false;
    }

    pendingRef.current = true;
    setClaiming(true);
    setScanError('');

    try {
      const result = await claimOrderByLabel(userTag, payload);
      const orderId = result.order.orderId;
      if (orderId == null || orderId === '') {
        setScanError('Claim succeeded but order id was missing.');
        return false;
      }
      claimedOrderIdRef.current = orderId;
      prefetchOrderDetail(orderId);
      return true;
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
        const body = err.response.data as { error?: string };
        setScanError(body.error ?? getApiErrorMessage(err, 'Failed to claim order'));
      } else {
        setScanError(getApiErrorMessage(err, 'Failed to claim order'));
      }
      return false;
    } finally {
      setClaiming(false);
      pendingRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div>Loading...</div>
      </div>
    );
  }

  if (tagError || userTag == null) {
    return (
      <div className="error-container">
        <div>Error: {tagError ?? 'No user tag'}</div>
      </div>
    );
  }

  return (
    <OrderLabelScanSheet
      title="Scan an order label"
      heading="Scan an order label"
      closeLabel="Back to order management"
      error={scanError}
      submitting={claiming}
      busyLabel="Claiming order"
      onCapture={handleQrCapture}
      onClose={() => { navigate('/order-manager', { replace: true }); }}
      onSuccessNavigate={openOrder}
    />
  );
};

export default ClaimOrderScanner;
